import PropTypes from 'prop-types';
import React from 'react';

import AppErrorFallback from './AppErrorFallback';

/**
 * Application level Error Boundary.
 *
 * Until TS-9461/TS-9473 the only error boundaries in the app were scoped to a
 * single subtree (ThemeErrorBoundary, MapErrorBoundary), so any render throw
 * outside those two subtrees propagated to the root and React unmounted the
 * whole tree, leaving a blank page with no way back other than closing the tab.
 *
 * This boundary wraps the app body so a throw from the navbar or any routed
 * page degrades to a recoverable fallback instead of a blank page.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    // Clear the fallback when the route changes. Without this the boundary
    // stays latched on `hasError` for the life of the session, so once a
    // second boundary exists lower in the tree — or this one moves below the
    // navbar — the user would navigate away and still be looking at the
    // fallback for a route that never threw. Today the navbar renders inside
    // this boundary, so the fallback's own buttons are the only way out and
    // the bug is masked; that is a property of the current mount point, not
    // of this class, so the reset lives here rather than relying on it.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.handleReset();
    }
  }

  componentDidCatch(error, errorInfo) {
    // Never swallow an app level crash: this is the last boundary before the
    // page goes blank, so it is logged in every environment, not just dev.
    // eslint-disable-next-line no-console
    console.error('AppErrorBoundary caught an error:', error, errorInfo);

    // Store error details in state for debugging
    this.setState({
      error,
      errorInfo,
    });
  }

  /**
   * Clears the boundary so children remount with fresh component state. React
   * unmounted the subtree when it caught, so this is a genuine remount and any
   * local state that triggered the throw (an open modal, a search term) is
   * gone. This is what lets the user recover without closing the tab.
   */
  handleReset() {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <AppErrorFallback
          error={error}
          errorInfo={errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return children;
  }
}

AppErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  /**
   * Changes whenever the route does. The boundary resets when it changes so a
   * caught error does not outlive the page that threw it. Supplied by the
   * wrapper in ./index.js; no call site passes it directly.
   */
  // eslint-disable-next-line react/forbid-prop-types
  resetKey: PropTypes.any,
};

export default AppErrorBoundary;
